/**
 * @fileOverview This file contains the implementation of the ModalContact component used on the Navbar component.
 * @version 1.0.0
 * @since 1.0.0
 * @author Miguel Ángel Bermea Rodríguez
 */

import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Switch,
  TextField,
  Typography,
  styled,
} from '@mui/material';
import customAxios from '../../utils/customAxios';
import { useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { toast } from 'react-toastify';
import { GlobalVariables } from '../../globalVar';
import { useTranslationHook } from '../../hooks/UseTranslationHook';
import moment from 'moment';
import { getToast } from '../../utils/toastConfig';

function ModalContact(props) {
  const {
    contactModalTitle,
    contactModalEmail,
    contactModalEmailHelp,
    contactModalSubject,
    contactModalMessage,
    contactModalSend,
    contactModalTeamT,
    contactModalTeamC,
    contactModalAlertS,
    contactModalAlertE,
  } = useTranslationHook();

  var emailDefault = localStorage.getItem('currUserEmail');

  const [email, setEmail] = useState(emailDefault);
  const [toEmail, setToEmail] = useState('help@dabi.io');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');

  /**
   * Handles the change event of a radio component
   *
   * @param {Event} event - The event object from the radio change
   */
  const handleRadioChange = (event) => {
    const value = event.target.value;
    setToEmail(value);
  };

  /**
   * Sends an email using the current user's information
   *
   * @param {Event} event - The event object from the form submission
   */
  function contact(event) {
    event.preventDefault();
    var currUser = localStorage.getItem('currUserLogged');
    var datetime = moment().toISOString(true);

    customAxios
      .post(
        `${GlobalVariables.getURL()}sendEmail`,
        {
          id: currUser,
          from: email,
          to: toEmail,
          subject: subject,
          message: message,
          sent_date: datetime,
        },
        {
          headers: {
            Authorization: 'bearer ' + localStorage.getItem('token'),
          },
        }
      )
      .then(
        (response) => {
          hide();
        },
        (error) => {
          console.log(error);
          // Function to show an error alert
          getToast({ status: 'error', title: contactModalAlertE });
        }
      );
  }

  /**
   * Asynchronously resets form fields to their default values
   *
   * @async
   * @function
   * @example
   * // Usage
   * resetFormFields();
   */
  async function resetFormFields() {
    await props.onHide();
    setEmail(emailDefault);
    setToEmail('help@dabi.io');
    setSubject('');
    setMessage('');
  }

  /**
   * Hides the form/modal, shows a success alert, and resets the form fields
   *
   * @function
   * @example
   * // Usage
   * hide();
   */
  function hide() {
    props.onHide();
    // Function to show a success alert
    getToast({ status: 'success', title: contactModalAlertS });
    resetFormFields();
  }

  return (
    <Modal
      {...props}
      onClose={resetFormFields}
      onHide={resetFormFields}
      size='md'
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title className='modal-title'>{contactModalTitle}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <form id='contact-teams-form' onSubmit={contact}>
          <div className='row'>
            <div className='col-md-8'>
              <TextField
                sx={{ width: '100%' }}
                label={contactModalEmail}
                type='email'
                value={email}
                helperText={contactModalEmailHelp}
                onChange={(e) => setEmail(e.target.value)}
                required
              />
            </div>
            <div className='col-md-4'>
              <RadioGroup value={toEmail} onChange={handleRadioChange}>
                <FormControlLabel
                  value='help@dabi.io'
                  control={<Radio />}
                  label={
                    <Typography variant='caption'>
                      {contactModalTeamT}
                    </Typography>
                  }
                  labelPlacement='end'
                />

                <FormControlLabel
                  value='hey@dabi.io'
                  control={<Radio />}
                  label={
                    <Typography variant='caption'>
                      {contactModalTeamC}
                    </Typography>
                  }
                  labelPlacement='end'
                />
              </RadioGroup>
            </div>
          </div>
          <br />
          <div className='row'>
            <div className='col-md-12'>
              <TextField
                sx={{ width: '100%' }}
                label={contactModalSubject}
                type='text'
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                required
              />
            </div>
          </div>
          <br />
          <div className='row'>
            <div className='col-md-12'>
              <TextField
                sx={{ width: '100%' }}
                label={contactModalMessage}
                type='text'
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                multiline
                rows={5}
                required
              />
            </div>
          </div>
        </form>
      </Modal.Body>
      <Modal.Footer>
        <button
          type='submit'
          form='contact-teams-form'
          className='save-modal-button'
        >
          {contactModalSend}
        </button>
      </Modal.Footer>
    </Modal>
  );
}

export default ModalContact;
